import { useMemo } from 'react'

// Define local types for @liquid-svg-glass/core
interface LocalGeometryConfig {
  width: number
  height: number
  radius?: number
  border?: number
}

interface LocalVisualConfig {
  blur?: number
  frost?: number
  scale?: number
  alpha?: number
  [key: string]: any
}

type GeometryConfig = LocalGeometryConfig
type VisualConfig = LocalVisualConfig

// Default filter attributes structure for type safety
export interface DefaultFilterAttributes {
  red: { xChannelSelector: string; yChannelSelector: string; scale: number }
  green: { xChannelSelector: string; yChannelSelector: string; scale: number }
  blue: { xChannelSelector: string; yChannelSelector: string; scale: number }
  gaussianBlur: { stdDeviation: number }
}

/**
 * Hook genérico para crear efectos liquid glass
 *
 * @description
 * Genera un displacement map SVG y atributos de filtro usando la librería @liquid-svg-glass/core.
 * Memoiza el resultado para evitar recalcular en cada render.
 *
 * NOTE: This is an optional feature. If @liquid-svg-glass/core is not installed,
 * this hook will return empty/default values.
 *
 * @example
 * ```tsx
 * const glass = useLiquidGlass(
 *   { width: 240, height: 48, radius: 12, border: 0.07 },
 *   { frost: 0.05, blur: 8, scale: -200, ... }
 * )
 *
 * <feImage href={glass.dataUri} result="map" />
 * ```
 */
export function useLiquidGlass(
  geometry: GeometryConfig,
  visual: VisualConfig,
  enableChromaticAberration = false
) {
  return useMemo(() => {
    // Default return when @liquid-svg-glass/core is not available
    return {
      dataUri: '',
      filterAttributes: {
        red: { xChannelSelector: 'R', yChannelSelector: 'R', scale: 0 },
        green: { xChannelSelector: 'G', yChannelSelector: 'G', scale: 0 },
        blue: { xChannelSelector: 'B', yChannelSelector: 'B', scale: 0 },
        gaussianBlur: { stdDeviation: 0 }
      } as DefaultFilterAttributes,
      chromaticAberrationSVG: null,
      calculatedGeometry: null
    }
  }, [
    // Geometry dependencies
    geometry.width,
    geometry.height,
    geometry.radius,
    geometry.border,
    // Visual dependencies
    visual.frost,
    visual.blur,
    visual.blurBackground,
    visual.displace,
    visual.scale,
    visual.alpha,
    visual.lightness,
    visual.x,
    visual.y,
    visual.blend,
    visual.r,
    visual.g,
    visual.b,
    enableChromaticAberration
  ])
}
